import { ReactNode, Component, PureComponent, ReactElement, RefObject, createRef } from 'react';
import _ from 'lodash';
import { SettingsGroup, SettingsType } from "@Primitive/SettingsListType";
import { SettingsList } from "@Primitive/SettingsList";
import { isDefined, IsNullOrEmpty, IsNullOrWhiteSpace, isNumeric } from "@Utils/Micro";
import { WebThingPort, WebThing } from "@API/WebThing";
import { WebThingPortType } from '@API/WebNodeTypes';
import { Plegma } from "@API/Api";
import { WebNode } from "@API/WebNode";
import { StateStatus } from "@API/WebNodeTypes";
import { IWidgetSettingsProps, WidgetDescriptionToPlegmaThing, IWidgetDescription, IWidgetDataValidateResp } from '@UI/Widgets/WidgetStructs';
import { PartialIWidgetThingsProps, IconURIPath, WidgetLocalizationKeys } from '../WidgetsThingsStructs';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import "./FileUpload.less";
// =================================================================================================
export interface FileUploadData {
    postURL: string;
    method: "post" | "put";
    withCredentials: boolean;
    supportsResumable: boolean;
    authorizationType: AuthorizationType;
    authorizationUsername: string;
    authorizationPassword: string;
    sizeLimit: number;
    showChooseFileButton: boolean;

    // Theme
    iconSize: number;
    iconColor: string;
    fontSize: number;
    fontColor: string;
    errorFontColor: string;
    backgroundColor: string;
}
// -------------------------------------------------------------------------------------------------
export const enum FileUploadStatus {
    Idle,
    Uploading,
    Error,
    Uploaded,
    Paused // for ResumableJS
}
// -------------------------------------------------------------------------------------------------
export interface FileUploadState {
    dragging: boolean;
    droppedFile: File;
    uploadProgress: number;
    status: FileUploadStatus;
}
// -------------------------------------------------------------------------------------------------
interface FileUploadInfo {
    postURL: string;
    method: "post" | "put";
    withCredentials: boolean;
    supportsResumable: boolean;
    authorizationType: AuthorizationType;
    authorizationUsername: string;
    authorizationPassword: string;
    headers: { [keys: string]: string };
    sizeLimit: number;
}
// -------------------------------------------------------------------------------------------------
export const enum FileUploadPortKeys {
    Setup = 'Setup',
    Success = 'Success' // -> result port
}
// -------------------------------------------------------------------------------------------------
const enum AuthorizationType {
    NoAuth = 'no-auth',
    BasicAuth = 'basic-auth'
}
// -------------------------------------------------------------------------------------------------
interface FileUploadResult {
    fileName: string;
    isSuccess: boolean;
    errorMsg: string;
}
type stringFunc = () => string;
// -------------------------------------------------------------------------------------------------
export class FileUpload extends Component<PartialIWidgetThingsProps<FileUploadData>, FileUploadState> {
    private dropRef: RefObject<HTMLDivElement> = createRef();
    private dragCounter = 0;
    private isAdvancedUpload = false;
    // ---------------------------------------------------------------------------------------------
    constructor(props: PartialIWidgetThingsProps<FileUploadData>) {
        super(props);
        this.state = {
            dragging: false,
            droppedFile: null,
            uploadProgress: -2,
            status: FileUploadStatus.Idle
        };

        var div = document.createElement('div');
        this.isAdvancedUpload = (('draggable' in div) || ('ondragstart' in div && 'ondrop' in div)) && 'FormData' in window && 'FileReader' in window;
    }
    // ---------------------------------------------------------------------------------------------
    get getPostURL() {
        if (_.has(this.props.PortsStates, FileUploadPortKeys.Setup)) {
            let state = this.props.PortsStates[FileUploadPortKeys.Setup];
            if (state.LastStateStatus == StateStatus.Valid) {
                let setup: FileUploadInfo = state.State;
                if (!IsNullOrWhiteSpace(setup.postURL)) {
                    return setup.postURL;
                }
            }
        }
        return this.props.WidgetData.postURL;
    }
    // ---------------------------------------------------------------------------------------------
    get getMethod() {
        if (_.has(this.props.PortsStates, FileUploadPortKeys.Setup)) {
            let state = this.props.PortsStates[FileUploadPortKeys.Setup];
            if (state.LastStateStatus == StateStatus.Valid) {
                let setup: FileUploadInfo = state.State;
                if (!IsNullOrWhiteSpace(setup.method)) {
                    return setup.method;
                }
            }
        }
        return this.props.WidgetData.method;
    }
    // ---------------------------------------------------------------------------------------------
    get getBasicAuth() {
        if (_.has(this.props.PortsStates, FileUploadPortKeys.Setup)) {
            let state = this.props.PortsStates[FileUploadPortKeys.Setup];
            if (state.LastStateStatus == StateStatus.Valid) {
                let setup: FileUploadInfo = state.State;
                if (setup.authorizationType == AuthorizationType.BasicAuth)
                    return "Basic " + btoa(setup.authorizationUsername + ":" + setup.authorizationPassword);
                return null;
            }
        }
        if (this.props.WidgetData.authorizationType == AuthorizationType.BasicAuth)
            return "Basic " + btoa(this.props.WidgetData.authorizationUsername + ":" + this.props.WidgetData.authorizationPassword);
        return null;
    }
    // ---------------------------------------------------------------------------------------------
    get getWithCredentials() {
        if (_.has(this.props.PortsStates, FileUploadPortKeys.Setup)) {
            let state = this.props.PortsStates[FileUploadPortKeys.Setup];
            if (state.LastStateStatus == StateStatus.Valid) {
                let setup: FileUploadInfo = state.State;
                if (_.has(setup, "withCredentials")) {
                    return setup.withCredentials;
                }
            }
        }
        return this.props.WidgetData.withCredentials;
    }
    // ---------------------------------------------------------------------------------------------
    get getResumableSupport() {
        if (_.has(this.props.PortsStates, FileUploadPortKeys.Setup)) {
            let state = this.props.PortsStates[FileUploadPortKeys.Setup];
            if (state.LastStateStatus == StateStatus.Valid) {
                let setup: FileUploadInfo = state.State;
                if (_.has(setup, "supportsResumable")) {
                    return setup.supportsResumable;
                }
            }
        }
        return this.props.WidgetData.supportsResumable;
    }
    // ---------------------------------------------------------------------------------------------
    get getHeaders() {
        if (_.has(this.props.PortsStates, FileUploadPortKeys.Setup)) {
            let state = this.props.PortsStates[FileUploadPortKeys.Setup];
            if (state.LastStateStatus == StateStatus.Valid) {
                let setup: FileUploadInfo = state.State;
                if (_.has(setup, "headers")) {
                    return setup.headers;
                }
            }
        }
        return {};
    }
    // ---------------------------------------------------------------------------------------------
    get getSizeLimit() {
        if (_.has(this.props.PortsStates, FileUploadPortKeys.Setup)) {
            let state = this.props.PortsStates[FileUploadPortKeys.Setup];
            if (state.LastStateStatus == StateStatus.Valid) {
                let setup: FileUploadInfo = state.State;
                if (isNumeric(setup.sizeLimit)) {
                    return setup.sizeLimit;
                }
            }
        }
        return this.props.WidgetData.sizeLimit;
    }
    // ---------------------------------------------------------------------------------------------
    componentDidMount() {
        this.dragCounter = 0;
        let div = this.dropRef.current;
        div.addEventListener('dragenter', this.handleDragIn);
        div.addEventListener('dragleave', this.handleDragOut);
        div.addEventListener('dragover', this.handleDrag);
        div.addEventListener('drop', this.handleDrop);
    }
    // ---------------------------------------------------------------------------------------------
    componentWillUnmount() {
        let div = this.dropRef.current;
        div.removeEventListener('dragenter', this.handleDragIn);
        div.removeEventListener('dragleave', this.handleDragOut);
        div.removeEventListener('dragover', this.handleDrag);
        div.removeEventListener('drop', this.handleDrop);
    }
    // ---------------------------------------------------------------------------------------------
    handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
    }
    // ---------------------------------------------------------------------------------------------
    handleDragIn = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.state.status == FileUploadStatus.Uploading)
            return;

        this.dragCounter++;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            this.setState({ dragging: true });
        }
    }
    // ---------------------------------------------------------------------------------------------
    handleDragOut = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.state.status == FileUploadStatus.Uploading)
            return;

        this.dragCounter--
        if (this.dragCounter > 0) return;
        this.setState({ dragging: false });
    }
    // ---------------------------------------------------------------------------------------------
    handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.state.status == FileUploadStatus.Uploading)
            return;

        this.setState({ dragging: false });
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            console.error(e.dataTransfer.files);

            this.setState({ droppedFile: e.dataTransfer.files[0] });

            e.dataTransfer.clearData();
            this.dragCounter = 0;
        }
    }
    // ---------------------------------------------------------------------------------------------
    uploadProgress = evt => {
        if (evt?.lengthComputable) {
            let percentComplete = Math.round(evt.loaded * 100 / evt.total);
            this.setState({ uploadProgress: percentComplete });
        } else {
            this.setState({ uploadProgress: -1 });
        }
    }
    // ---------------------------------------------------------------------------------------------
    uploadComplete = evt => {
        let statusCode = evt?.target?.status;
        let responseText = evt?.target?.responseText;

        this.setState({ status: statusCode == 200 ? FileUploadStatus.Uploaded : FileUploadStatus.Error });
        this.props.SendPortEvent(FileUploadPortKeys.Success, responseText);
    }
    // ---------------------------------------------------------------------------------------------
    uploadFailed = evt => {
        let responseText = evt?.target?.responseText;

        this.setState({ status: FileUploadStatus.Error });
        this.props.SendPortEvent(FileUploadPortKeys.Success, responseText);
    }
    // ---------------------------------------------------------------------------------------------
    uploadCanceled = () => this.setState({ status: FileUploadStatus.Error })
    // ---------------------------------------------------------------------------------------------
    startUploading = () => {
        if (this.state.droppedFile == null)
            return;
        if (IsNullOrWhiteSpace(this.getPostURL))
            return;

        if (this.getResumableSupport == true) {
            import("resumablejs").then((Resumable) => {
                var chunkSize = 1 * 1024 * 1024;
                var r = new Resumable.default({
                    chunkSize: chunkSize, //Default 1*1024*1024
                    target: this.getPostURL,
                    testChunks: true,
                    maxFiles: 1,
                    maxChunkRetries: null,
                    withCredentials: this.getWithCredentials,
                    headers: this.getHeaders,
                    generateUniqueIdentifier: (file => {
                        return new Promise((resolve, reject) => {
                            import("xxhashjs").then(XXH => {
                                var hasher = XXH.h32(0);
                                var off = 0;
                                const reader = new FileReader();

                                reader.onerror = () => {
                                    reader.abort();

                                    reject(() => {
                                        console.error("Problem reading input file");
                                    });
                                };

                                reader.onload = () => {
                                    hasher.update(reader.result);
                                    off += chunkSize;
                                    if (off >= file.size) {
                                        var digest = hasher.digest().toString(16);
                                        const relativePath = file.webkitRelativePath || file.relativePath || file.fileName || file.name;
                                        var nameDigest = XXH.h32(relativePath, 0).toString(16);

                                        resolve('f' + nameDigest + '-' + file.size + '-' + digest);
                                    }
                                    else {
                                        if (off + chunkSize < file.size)
                                            reader.readAsArrayBuffer(file.slice(off, off + chunkSize));
                                        else
                                            reader.readAsArrayBuffer(file.slice(off, file.size));
                                    }
                                };

                                if (file.size < chunkSize)
                                    reader.readAsArrayBuffer(file);
                                else
                                    reader.readAsArrayBuffer(file.slice(0, chunkSize));
                            });
                        });
                    }) as unknown as stringFunc
                });
                r.on('fileAdded', (file) => {
                    console.log("[RESUMABLE][ADDED]", file);
                    r.upload();
                });

                r.on('fileSuccess', (file) => {
                    console.log("[RESUMABLE][SUCCESS]", file);

                    this.setState({ status: FileUploadStatus.Uploaded });
                    let res: FileUploadResult = { fileName: file.fileName, isSuccess: true, errorMsg: null };
                    this.props.SendPortEvent(FileUploadPortKeys.Success, res);
                });

                r.on('progress', () => {
                    let percentageComplete = Math.round(100 * r.progress());
                    console.log("[RESUMABLE][PROGRESS] -> " + percentageComplete + " %");
                    this.setState({ uploadProgress: percentageComplete });
                });

                r.on('uploadStart', () => {
                    this.setState({ uploadProgress: 0, status: FileUploadStatus.Uploading });
                    console.log("[RESUMABLE][UPLOADSTART]");
                });

                r.on('complete', () => {
                    this.setState({ status: FileUploadStatus.Uploaded });
                    console.log("[RESUMABLE][COMPLETE]");
                });

                r.on('cancel', () => {
                    console.log("[RESUMABLE][CANCEL]");
                    this.setState({ status: FileUploadStatus.Error });
                });

                r.on('pause', () => {
                    if (r.files.length > 0) {
                        if (r.isUploading()) {
                            this.setState({ status: FileUploadStatus.Paused });
                            console.log("[RESUMABLE][PAUSE]");
                            return r.pause();
                        }

                        this.setState({ status: FileUploadStatus.Uploading });
                        console.log("[RESUMABLE][RESUME]");
                        return r.upload();
                    }
                });

                r.on('error', (message, file) => {
                    console.error("[RESUMABLE][ERROR]", file, message);
                    this.setState({ status: FileUploadStatus.Error });

                    let res: FileUploadResult = { fileName: file.fileName, isSuccess: false, errorMsg: message };
                    this.props.SendPortEvent(FileUploadPortKeys.Success, res);
                });

                // Add file for uploading
                r.addFile(this.state.droppedFile);
            });
        } else {
            this.setState({ uploadProgress: 0, status: FileUploadStatus.Uploading });

            var xhr = new XMLHttpRequest();

            /* event listeners */
            xhr.upload.addEventListener("progress", this.uploadProgress, false);
            xhr.addEventListener("load", this.uploadComplete, false);
            xhr.addEventListener("error", this.uploadFailed, false);
            xhr.addEventListener("abort", this.uploadCanceled, false);

            /* Be sure to change the url below to the url of your upload server side script */
            xhr.open(this.getMethod.toUpperCase(), this.getPostURL);
            xhr.withCredentials = this.getWithCredentials;

            let basicAuth = this.getBasicAuth;
            if (!IsNullOrWhiteSpace(basicAuth))
                xhr.setRequestHeader("Authorization", basicAuth);

            // pass all the headers
            let headers = this.getHeaders;
            for (var h in headers)
                xhr.setRequestHeader(h, headers[h]);

            // Form data
            var formData = new FormData();
            formData.append("file1", this.state.droppedFile);
            xhr.send(formData);
        }
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let fileSize = "";
        let formClassName = "file-upload-box";

        if (this.isAdvancedUpload)
            formClassName += " has-advanced-upload";
        if (this.state.dragging)
            formClassName += " is-dragover";

        let droppedFile = this.state.droppedFile;
        let uploadStatus = this.state.status;
        let uploadProgress = this.state.uploadProgress;
        let widgetData = this.props.WidgetData;
        let sizeLimit = this.getSizeLimit;
        let exceedsSizeLimit = false;
        let uploading = uploadStatus == FileUploadStatus.Uploading;

        if (droppedFile != null) {
            if (droppedFile.size > 1024 * 1024)
                fileSize = (Math.round(droppedFile.size * 100 / (1024 * 1024)) / 100).toString() + 'MB';
            else
                fileSize = (Math.round(droppedFile.size * 100 / 1024) / 100).toString() + 'KB';

            if (!uploading && !IsNullOrWhiteSpace(this.getPostURL))
                formClassName += " is-dropped";

            // is upload allowed?
            exceedsSizeLimit = isNumeric(sizeLimit) && sizeLimit * 1024 < droppedFile.size;
        }
        if (uploadStatus == FileUploadStatus.Error) {
            formClassName += " is-error";
        }
        if (uploadStatus == FileUploadStatus.Uploaded) {
            formClassName += " is-success";
        }
        if (uploadStatus == FileUploadStatus.Paused) {
            // TODO
        }

        return <div ref={this.dropRef} className={formClassName}
            style={{
                color: widgetData.fontColor,
                fontSize: widgetData.fontSize,
                backgroundColor: widgetData.backgroundColor
            }}>
            <div className="box__input">
                <svg
                    style={{
                        maxWidth: widgetData.iconSize,
                        color: widgetData.iconColor
                    }}
                    aria-hidden="true" focusable="false" data-prefix="fad" data-icon="upload"
                    role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"
                    className="svg-inline--fa box__icon">
                    <g className="fa-group"><path fill="currentColor" d="M488 351.92H352v8a56 56 0 0 1-56 56h-80a56 56 0 0 1-56-56v-8H24a23.94 23.94 0 0 0-24 24v112a23.94 23.94 0 0 0 24 24h464a23.94 23.94 0 0 0 24-24v-112a23.94 23.94 0 0 0-24-24zm-120 132a20 20 0 1 1 20-20 20.06 20.06 0 0 1-20 20zm64 0a20 20 0 1 1 20-20 20.06 20.06 0 0 1-20 20z" className="fa-secondary"></path><path fill="currentColor" d="M192 359.93v-168h-87.7c-17.8 0-26.7-21.5-14.1-34.11L242.3 5.62a19.37 19.37 0 0 1 27.3 0l152.2 152.2c12.6 12.61 3.7 34.11-14.1 34.11H320v168a23.94 23.94 0 0 1-24 24h-80a23.94 23.94 0 0 1-24-24z" className="fa-primary"></path></g>
                </svg>
                <input className="box__file" type="file" name="files[]" id="file" data-multiple-caption="{count} files selected" multiple />

                {droppedFile != null
                    ? <label>{droppedFile.name} ({fileSize})</label>
                    : <span className="box__dragndrop">Drop file here to upload or</span>}

                {widgetData.showChooseFileButton ?
                    <button className={`choose__file box__button${uploading ? ' disabled' : ''}`}>
                        <label htmlFor='choose__file'>{'Choose file'}</label>
                        <input
                            id='choose__file'
                            type='file'
                            disabled={uploading}
                            onChange={event => {
                                // keep file in state
                                let input = event.target as HTMLInputElement;
                                let file = input.files?.item(0);
                                this.setState({ droppedFile: file });
                            }} />
                    </button> : null}

                <button
                    className={`box__button${exceedsSizeLimit ? ' disabled' : ''}`}
                    onClick={exceedsSizeLimit ? undefined : this.startUploading}>
                    {'Upload'}
                </button>
            </div>

            <div className="box__success">The file was uploaded successfully</div>
            <div className="box__error" style={{ color: widgetData.errorFontColor }}>The file could not be uploaded</div>

            {exceedsSizeLimit
                ? <div style={{ color: '#f44336', padding: 10 }}>
                    {`The file size exceeds the limit of ${sizeLimit}KB allowed and cannot be uploaded`}
                </div>
                : null}

            {uploadProgress > -1 && uploading ?
                <div className="box__progress">
                    <input type="checkbox" id="water" />
                    <label htmlFor="water">
                        <div id="fill" style={{ width: uploadProgress + "%" }} />
                    </label>
                    <span id="progress">{`${uploadProgress}% uploaded`}</span>
                    <div style={{ padding: '8px 0', margin: 20 }} className="loader">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
                : null}
        </div>;
    }
}
// =================================================================================================
class FileUploadSettings extends PureComponent<IWidgetSettingsProps<FileUploadData>> {
    constructor(props: IWidgetSettingsProps<FileUploadData>) {
        super(props);
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<FileUploadData>[] = [{
            name: "",
            elements: [{
                label: "URL",
                dataPath: "postURL",
                type: SettingsType.String,
                oneLine: false,
                tooltip: "URL for uploading"
            },
            {
                label: "Method",
                dataPath: "method",
                type: SettingsType.DropDown,
                items: [
                    {
                        value: "post",
                        label: "POST"
                    },
                    {
                        value: "put",
                        label: "PUT"
                    }
                ]
            },
            {
                label: "With Credentials",
                dataPath: "withCredentials",
                type: SettingsType.Boolean,
                tooltip: "Use cookies for the form uploading request"
            },
            {
                label: "Supports Resumable",
                dataPath: "supportsResumable",
                type: SettingsType.Boolean,
                tooltip: "Resumable upload functionality (should be supported by the remote file server)"
            },
            {
                type: SettingsType.Number,
                label: 'Size Limit (in KB)',
                dataPath: 'sizeLimit',
                tooltip: 'The maximum file size allowed in KB. For no limit leave it empty.',
                oneLine: true
            },
            {
                label: 'Choose File Button',
                dataPath: 'showChooseFileButton',
                type: SettingsType.Boolean,
                tooltip: 'Allows to select the file to upload from a popup box'
            },
            {
                type: SettingsType.Group,
                name: "Authorization",
                dataPath: "",
                elements: [{
                    label: "Type",
                    dataPath: "authorizationType",
                    type: SettingsType.DropDown,
                    items: [
                        {
                            value: AuthorizationType.NoAuth,
                            label: "No Auth"
                        },
                        {
                            value: AuthorizationType.BasicAuth,
                            label: "Basic Auth"
                        }
                    ]
                },
                {
                    type: SettingsType.String,
                    label: "Username",
                    dataPath: "authorizationUsername",
                    isHidden: (data) => data.authorizationType == AuthorizationType.NoAuth
                },
                {
                    type: SettingsType.String,
                    label: "Password",
                    dataPath: "authorizationPassword",
                    isHidden: (data) => data.authorizationType == AuthorizationType.NoAuth
                }]
            },
            {
                type: SettingsType.Group,
                name: "Theme",
                dataPath: "",
                elements: [{
                    type: SettingsType.Slider,
                    label: "Icon Size",
                    dataPath: "iconSize",
                    maxValue: 200,
                    minValue: 10,
                    stepValue: 5,
                    valuePosfix: "px"
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Icon Color",
                    dataPath: "iconColor"
                },
                {
                    type: SettingsType.Slider,
                    label: "Font Size",
                    dataPath: "fontSize",
                    maxValue: 32,
                    minValue: 8,
                    stepValue: 1,
                    valuePosfix: "px"
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Font Color",
                    dataPath: "fontColor"
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Error Font Color",
                    dataPath: "errorFontColor"
                },
                {
                    type: SettingsType.ColorPicker,
                    label: "Background Color",
                    dataPath: "backgroundColor"
                },
                ]
            }]
        }];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isDefined(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
        />;
    }
}
// =================================================================================================
export class FileUploadWidget extends IWidgetThingsClass<FileUploadData> {
    Render(): ReactNode {
        return <FileUpload />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <FileUploadSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort(
            {
                Name: 'Setup',
                PortKey: FileUploadPortKeys.Setup,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            },
            WebThingPortType.Data
        ));

        ports.push(new WebThingPort(
            {
                Name: 'Result',
                PortKey: FileUploadPortKeys.Success,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            },
            WebThingPortType.Data
        ));

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        // Add thing's ports.
        webThing.Ports = {};
        this.createThingPorts().forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts();
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): FileUploadData {
        return {
            // TODO: Set the default values for the widget
            postURL: "",
            withCredentials: true,
            supportsResumable: false,
            method: "post",
            authorizationType: AuthorizationType.NoAuth,
            authorizationUsername: "",
            authorizationPassword: "",
            iconSize: 100,
            iconColor: "#748c8e",
            sizeLimit: null,
            showChooseFileButton: true,
            fontSize: 16,
            fontColor: "#000000de",
            errorFontColor: "#f44336",
            backgroundColor: "#c8dadf",
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return FileUploadWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'File Uploader',
            WidgetTitle: '',
            Key: 'FileUploadWidget',
            Type: 'FileUploadWidget',
            Data: FileUploadWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: IconURIPath + '/svg/file_upload.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: FileUploadData): FileUploadData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}